import { useAtom, useAtomValue } from 'jotai';
import useOlEvent from '@/hooks/useOlEvent';
import { parcelLayer, selectedParcelLayer } from '@/ol/layers';
import { getSelectedParcelStyle } from '@/ol/styles';
import { selectedParcellesIduState, validatedParcellesIduState } from '@/store';

/**
 * Custom hook to handle MVT Cadastre Selection.
 * @param {Object} map - The OpenLayers Map instance.
 */
export default function useMVTCadastreSelection(map) {
  const validatedParcellesIdu = useAtomValue(validatedParcellesIduState);
  const [selectedParcellesIdu, setSelectedParcellesIdu] = useAtom(selectedParcellesIduState);

  // Set style for selected parcel layer
  selectedParcelLayer.setStyle((feature) => {
    if (selectedParcellesIdu.includes(feature.get('id'))) {
      return getSelectedParcelStyle(feature);
    }
  });

  /**
   * Handle map click event.
   * @param {Object} event - The map click event.
   */
  async function onMapClick(event) {
    // Return if zoom level is less than minimum zoom level of parcel layer or parcel layer is not visible
    if (event.map.getView().getZoom() < parcelLayer.getMinZoom() || !parcelLayer.getVisible()) {
      return;
    }

    const features = await parcelLayer.getFeatures(event.pixel);

    if (features.length > 0) {
      const [feature] = features;
      const fid = feature.get('id');

      // Return if parcel is already validated
      if (validatedParcellesIdu.includes(fid)) {
        return;
      }

      // If parcel is already selected, remove it from selection, otherwise add it to selection
      let updatedSelectedParcellesIdu;
      if (selectedParcellesIdu.includes(fid)) {
        updatedSelectedParcellesIdu = selectedParcellesIdu.filter((p) => p !== fid);
      } else {
        updatedSelectedParcellesIdu = [...selectedParcellesIdu, fid];
      }

      // Update selected parcels in state
      setSelectedParcellesIdu(updatedSelectedParcellesIdu);

      // Trigger a redraw of the selected parcel layer
      selectedParcelLayer.changed();
    }
  }

  // Attach click event listener to map
  useOlEvent(map, 'singleclick', onMapClick);
}
